/**
 * The language select, as Settings has it (TMMAPP-0113 item 3, the 0098 follow-up): the choice
 * applies at once (i18n sets `<html lang>` and keeps `tmm_locale`), then is saved as the person's
 * `locale` when they have a profile. A failed save leaves the browser's choice in place. `quiet`
 * is the small form for a dialog's header (the welcome dialog), where the label is visually hidden.
 */
import { useId, useState } from 'react';
import { FormControl, InputLabel, MenuItem, Select } from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import { useTranslation } from 'react-i18next';
import { patchMe } from '@/api/settings';
import { DEFAULT_LOCALE, normalizeLocale, SUPPORTED_LOCALES } from '@/i18n/constants';
import { useMe, useMeRefetch } from '@/session/meHooks';

export function LanguageSelect({ quiet = false }: { quiet?: boolean }) {
  const { t, i18n } = useTranslation();
  const me = useMe();
  const refetchMe = useMeRefetch();
  const labelId = useId();
  const [busy, setBusy] = useState(false);
  const current = normalizeLocale(i18n.resolvedLanguage ?? i18n.language) ?? DEFAULT_LOCALE;

  async function change(code: string) {
    const next = normalizeLocale(code) ?? DEFAULT_LOCALE;
    if (busy || next === current) return;
    setBusy(true);
    await i18n.changeLanguage(next);
    try {
      if (me.user.id !== null) {
        await patchMe({ locale: next });
        await refetchMe();
      }
    } catch {
      // The browser keeps the language (`tmm_locale`); only the saved preference didn't update.
    } finally {
      setBusy(false);
    }
  }

  return (
    <FormControl size="small" sx={{ minWidth: quiet ? 140 : 260 }}>
      <InputLabel id={labelId} sx={quiet ? visuallyHidden : undefined}>
        {t('language.label')}
      </InputLabel>
      <Select
        labelId={labelId}
        label={quiet ? undefined : t('language.label')}
        value={current}
        // Not disabled while saving: a disabled control drops focus out of a modal.
        aria-busy={busy || undefined}
        onChange={(event) => void change(String(event.target.value))}
        sx={quiet ? { fontSize: 13 } : undefined}
      >
        {SUPPORTED_LOCALES.map((code) => (
          <MenuItem key={code} value={code} lang={code}>
            {t(`language.${code}`)}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}
